/**
 * Dev login bypass gate + token store registry. Platform-neutral: each entry
 * (`../react`, `../react-native`) registers its own store from `configureAuth`,
 * so nothing in `core/` touches `localStorage` or `react-native`.
 */

export type DevTokenStore = {
  get(): string | null;
  set(token: string): void;
  clear(): void;
  /** Subscribe to token changes. Returns an unsubscribe. */
  subscribe(callback: () => void): () => void;
};

/**
 * Whether the dev login bypass is on, and where its token lives. Set once per
 * app from that app's own compile-time env flag; this package never reads env
 * itself.
 */
let gate: { enabled: boolean; store: DevTokenStore } | null = null;

export const configureDevAuth = (enabled: boolean, store: DevTokenStore) => {
  gate = { enabled, store };
};

const getGate = () => {
  if (gate === null) {
    throw new Error('[cognito-auth] configureAuth() must run before any auth hook renders');
  }
  return gate;
};

export const isDevAuthEnabled = () => getGate().enabled;

/**
 * The stored dev bypass token, or null when absent or the bypass is off. An
 * expired or unreadable token is discarded rather than returned: callers prefer
 * a dev token over the real identity provider, so a stale one would otherwise
 * shadow Cognito forever and leave the app talking to a backend that rejects it.
 */
export const getDevToken = (): string | null => {
  const { enabled, store } = getGate();
  if (!enabled) return null;
  const token = store.get();
  if (token === null) return null;
  if (isExpired(token)) {
    store.clear();
    return null;
  }
  return token;
};

/**
 * Reads `exp` out of a compact JWT payload. The signature is not checked — the
 * backend is the only thing that can trust a token; this is purely about not
 * holding on to one that is already dead. Anything unparseable counts as
 * expired so a corrupt entry cannot wedge the app either.
 */
const isExpired = (token: string): boolean => {
  const payload = token.split('.')[1];
  if (payload === undefined) return true;
  try {
    const claims: unknown = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    if (typeof claims !== 'object' || claims === null || !('exp' in claims)) return true;
    const { exp } = claims;
    return typeof exp !== 'number' || Date.now() >= exp * 1000;
  } catch {
    return true;
  }
};

export const storeDevToken = (token: string): void => getGate().store.set(token);

export const clearDevToken = (): void => getGate().store.clear();

/**
 * A stored dev bypass token outranks Cognito in `useConvexAppAuth`, so any real
 * login must drop it first — otherwise Amplify signs the user in while Convex
 * keeps presenting the old dev token. No-op when the bypass is off.
 */
export const clearDevSession = () => {
  if (isDevAuthEnabled()) clearDevToken();
};

export const subscribeDevToken = (callback: () => void): (() => void) =>
  getGate().store.subscribe(callback);
